import { CalendarRange, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'

import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { useGantt } from '@/features/agenda/api'
import { Gantt } from '@/features/agenda/gantt'
import { useCategoriasAtivas } from '@/features/frota/api'
import { formatarData } from '@/lib/datas'
import { diaRelativo, paraInstante } from '@/features/precos/dialogo-de-bloqueio'
import { errorMessage } from '@/lib/api'

/**
 * Agenda — o calendário da frota, veículo a veículo.
 *
 * ---------------------------------------------------------------------------
 * A JANELA É POR DIAS, NÃO POR PAR DE DATAS
 * ---------------------------------------------------------------------------
 * A pergunta desta tela é "como está a frota NESTA semana/quinzena/mês", e por
 * isso o controle é um dia inicial + um tamanho (7/14/30) com ◀ ▶ pulando de
 * janela em janela — o mesmo gesto de qualquer agenda. Um par livre de datas
 * (como na disponibilidade) responderia outra pergunta, a do período de UMA
 * locação. Os atalhos ficam bem abaixo do teto da API (92 dias), então a
 * validação da janela nunca aparece para quem usa os controles.
 *
 * ---------------------------------------------------------------------------
 * O DIA DA TELA VIRA INSTANTE NO FIO
 * ---------------------------------------------------------------------------
 * O Gantt da API recebe INSTANTES com fuso (`filtroDoGanttSchema`), e o dia
 * escolhido vira o dia local INTEIRO — 00:00 a 23:59 em `America/Sao_Paulo`,
 * via `paraInstante` — senão a janela "de 15/03" começaria às 21h do dia 14 em
 * todo fuso negativo.
 */

const UM_DIA_EM_MS = 86_400_000

const ATALHOS_DE_JANELA = [7, 14, 30] as const

type TamanhoDaJanela = (typeof ATALHOS_DE_JANELA)[number]

/** O dia de calendário `n` dias adiante. `''` quando a base não é uma data. */
function somarDias(dia: string, dias: number): string {
  const base = Date.parse(`${dia}T12:00:00Z`)

  if (Number.isNaN(base)) {
    return ''
  }

  return new Date(base + dias * UM_DIA_EM_MS).toISOString().slice(0, 10)
}

export function AgendaPage(): ReactElement {
  const [inicioDaJanela, setInicioDaJanela] = useState(() => diaRelativo(0))
  const [diasDaJanela, setDiasDaJanela] = useState<TamanhoDaJanela>(14)
  const [categoriaDoGantt, setCategoriaDoGantt] = useState('')

  const categorias = useCategoriasAtivas()

  const fimDaJanela = somarDias(inicioDaJanela, diasDaJanela - 1)
  const deIso = inicioDaJanela.length === 0 ? null : paraInstante(`${inicioDaJanela}T00:00`)
  const ateIso = fimDaJanela.length === 0 ? null : paraInstante(`${fimDaJanela}T23:59`)

  // Data em branco desliga a consulta, e query desligada guarda o resultado
  // anterior (`placeholderData`): sem esta frase, apagar o dia inicial deixaria
  // o calendário da janela antiga na tela como resposta da pergunta atual.
  const avisoDaJanela =
    deIso === null || ateIso === null
      ? 'informe o primeiro dia da janela para montar o calendário'
      : null

  const parametrosDoGantt = useMemo(
    () => ({
      de: deIso ?? '',
      ate: ateIso ?? '',
      categoryId: categoriaDoGantt,
    }),
    [deIso, ateIso, categoriaDoGantt],
  )

  const consulta = useGantt(parametrosDoGantt)

  function navegar(direcao: -1 | 1): void {
    const base = inicioDaJanela.length === 0 ? diaRelativo(0) : inicioDaJanela
    const proximo = somarDias(base, direcao * diasDaJanela)

    setInicioDaJanela(proximo.length === 0 ? diaRelativo(0) : proximo)
  }

  const listaDeCategorias = categorias.data?.itens ?? []
  const dados = avisoDaJanela === null ? consulta.data : undefined
  const janelaVazia = dados?.veiculos.length === 0 && dados.naoAlocadas.length === 0

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Agenda da frota</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          Cada veículo com o que ocupa a agenda dele — reservas em amarelo, manutenção e limpeza em
          cinza — e, embaixo, as reservas aprovadas que ainda esperam um carro. Reserva que ainda
          não saiu do pátio pode ser movida de veículo pela alça da barra: arraste para outra linha
          ou clique nela para escolher o destino.
        </p>
      </div>

      {categorias.isError ? (
        <Alert variant="warning">
          <AlertTitle>Não foi possível carregar as categorias</AlertTitle>
          <AlertDescription>
            {errorMessage(categorias.error)} O seletor de categoria fica vazio até a lista voltar.
          </AlertDescription>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => {
              void categorias.refetch()
            }}
          >
            <RefreshCw aria-hidden="true" />
            Tentar de novo
          </Button>
        </Alert>
      ) : null}

      {/* ------------------------------------------------------------------
          Controles da janela
          ------------------------------------------------------------------ */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="agenda-inicio">Primeiro dia</Label>
          <Input
            id="agenda-inicio"
            type="date"
            value={inicioDaJanela}
            onChange={(evento) => {
              setInicioDaJanela(evento.target.value)
            }}
            className="tabular-money sm:w-44"
          />
        </div>

        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-foreground">Janela</span>
          <div className="flex items-center gap-1">
            {ATALHOS_DE_JANELA.map((tamanho) => (
              <Button
                key={tamanho}
                type="button"
                variant={tamanho === diasDaJanela ? 'secondary' : 'ghost'}
                size="sm"
                aria-pressed={tamanho === diasDaJanela}
                onClick={() => {
                  setDiasDaJanela(tamanho)
                }}
              >
                {tamanho} dias
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Janela anterior"
            className="size-10"
            onClick={() => {
              navegar(-1)
            }}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setInicioDaJanela(diaRelativo(0))
            }}
          >
            Hoje
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Próxima janela"
            className="size-10"
            onClick={() => {
              navegar(1)
            }}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="agenda-categoria">Categoria</Label>
          <Select
            id="agenda-categoria"
            value={categoriaDoGantt}
            onChange={(evento) => {
              setCategoriaDoGantt(evento.target.value)
            }}
            disabled={categorias.isPending}
            className="sm:w-52"
          >
            <option value="">{categorias.isPending ? 'Carregando…' : 'Todas'}</option>
            {listaDeCategorias.map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.nome}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {avisoDaJanela === null ? (
        <p className="text-xs tabular-money text-fg-muted">
          Janela de {formatarData(inicioDaJanela)} a {formatarData(fimDaJanela)}, no fuso do ponto
          de retirada.
        </p>
      ) : (
        <Alert variant="warning">
          <AlertDescription>{avisoDaJanela}</AlertDescription>
        </Alert>
      )}

      {/* O primeiro carregamento é esqueleto; do segundo em diante a janela
          anterior fica na tela enquanto a nova chega, e esta linha avisa que o
          desenho ainda vai mudar. */}
      {dados !== undefined && consulta.isFetching ? (
        <p role="status" className="text-xs text-fg-muted">
          Atualizando o calendário da janela…
        </p>
      ) : null}

      {avisoDaJanela !== null ? null : consulta.isPending ? (
        <LoadingState rotulo="Montando o calendário da frota…" linhas={6} />
      ) : consulta.isError ? (
        <ErrorState
          titulo="Não foi possível montar o calendário"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      ) : dados === undefined ? null : janelaVazia ? (
        <EmptyState
          icone={CalendarRange}
          titulo="Nada na agenda desta janela"
          descricao="Nenhum veículo com ocupação e nenhuma reserva esperando carro no recorte escolhido. Tire o filtro de categoria ou mude a janela."
        />
      ) : (
        <Gantt dados={dados} />
      )}
    </div>
  )
}
